import * as React from 'react';
import { Button } from '@/components/ui/Button';
import { ProgressBar } from './ProgressBar';
import clsx from 'clsx';
import type {
  SurveyQuestion,
  SurveyAnswer,
  SingleChoiceAnswer,
  RankedAnswer,
  MultiSelectAnswer,
  OpenTextAnswer,
  TripleResponseAnswer,
} from '@/types/survey';

import SingleChoiceQuestion from '@/features/create-echo/components/personality/questions/SingleChoiceQuestion';
import RankedQuestion from '@/features/create-echo/components/personality/questions/RankedQuestion';
import MultiSelectQuestion from '@/features/create-echo/components/personality/questions/MultiSelectQuestion';
import OpenTextQuestion from '@/features/create-echo/components/personality/questions/OpenTextQuestion';
import TripleResponseQuestion from '@/features/create-echo/components/personality/questions/TripleResponseQuestion';

interface QuestionCardProps {
  question: SurveyQuestion;
  currentIndex: number;
  totalQuestions: number;
  answer?: SurveyAnswer | undefined;
  onAnswer: (answer: SurveyAnswer) => void;
  onNext: () => Promise<void> | void;
  onPrevious: () => void;
  onSaveForLater: () => void;
  isSubmitting?: boolean;
}

export const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  currentIndex,
  totalQuestions,
  answer,
  onAnswer,
  onNext,
  onPrevious,
  onSaveForLater,
  isSubmitting = false,
}) => {
  // Dynamic component renderer
  const renderInput = () => {
    switch (question.type) {
      case 'single_choice':
        return (
          <SingleChoiceQuestion
            question={question}
            answer={answer as SingleChoiceAnswer | undefined}
            onChange={onAnswer as (ans: SingleChoiceAnswer) => void}
          />
        );
      case 'ranked':
        return (
          <RankedQuestion
            question={question}
            answer={answer as RankedAnswer | undefined}
            onChange={onAnswer as (ans: RankedAnswer) => void}
          />
        );
      case 'multi_select':
        return (
          <MultiSelectQuestion
            question={question}
            answer={answer as MultiSelectAnswer | undefined}
            onChange={onAnswer as (ans: MultiSelectAnswer) => void}
          />
        );
      case 'open_text':
        return (
          <OpenTextQuestion
            question={question}
            answer={answer as OpenTextAnswer | undefined}
            onChange={onAnswer as (ans: OpenTextAnswer) => void}
          />
        );
      case 'triple_response':
        return (
          <TripleResponseQuestion
            question={question}
            answer={answer as TripleResponseAnswer | undefined}
            onChange={onAnswer as (ans: TripleResponseAnswer) => void}
          />
        );
      default:
        return <div className='text-red-500'>Unsupported question type: {question.type}</div>;
    }
  };

  return (
    <div className='w-full rounded-20 bg-glass-card p-6 shadow-[0px_4px_20px_rgba(0,0,0,0.05)] md:p-8'>
      <ProgressBar current={currentIndex} total={totalQuestions} />

      {/* Question Header */}
      <h3 className='mb-2 font-sans text-20 font-semibold leading-[1.4] text-dark-100 md:text-24'>
        {question.question}
      </h3>

      {/* Instruction / Highlight */}
      {(question.instruction || question.highlight) && (
        <p
          className={clsx(
            'mb-6 font-sans text-14',
            question.highlight ? 'font-bold text-brand-600' : 'font-normal text-gray-500'
          )}
        >
          {question.highlight || question.instruction}
        </p>
      )}

      {/* Dynamic Input Area */}
      <div className={clsx('mb-10', isSubmitting && 'pointer-events-none opacity-50')}>
        {renderInput()}
      </div>

      {/* Footer Controls */}
      <div className='mt-auto flex flex-col items-center justify-between gap-6 md:flex-row md:gap-0'>
        <div className='flex w-full flex-row items-center gap-3 md:w-auto md:gap-4'>
          {currentIndex > 0 && (
            <Button
              onClick={onPrevious}
              disabled={isSubmitting}
              variant='gray-gradient-border'
              className='h-[48px] flex-1 rounded-30 md:w-[160px] md:flex-none'
            >
              PREVIOUS
            </Button>
          )}

          <Button
            onClick={onNext}
            disabled={isSubmitting}
            variant='purple-dark'
            className={clsx(
              'h-[48px] rounded-30 shadow-lg',
              'flex-1 md:w-[160px] md:flex-none',
              currentIndex === 0 && 'w-full'
            )}
          >
            {isSubmitting ? 'SAVING...' : 'NEXT'}
          </Button>
        </div>

        <button
          onClick={onSaveForLater}
          disabled={isSubmitting}
          className='font-sans text-16 font-semibold text-gray-650 underline decoration-solid transition-colors hover:text-brand-800 disabled:opacity-50'
        >
          Save for Later
        </button>
      </div>
    </div>
  );
};

export default QuestionCard;
